// Version checking service

const CURRENT_VERSION_KEY = 'app_version';
const VERSION_CHECK_INTERVAL = 300000; // 5 minutes (not used by default)
const VERSION_ENDPOINT = '/version.json'; // Will be served from public folder
// Версия, которая реально запущена (зашита в сборку при build).
const RUNNING_VERSION = process.env.REACT_APP_VERSION || null;
// Защита от петли «обновились → синк → снова обновление»: перезагрузка ради
// одной и той же версии — не чаще раза в 10 минут.
const AUTO_UPDATE_KEY = 'autoUpdateTried';
const AUTO_UPDATE_RETRY_MS = 10 * 60 * 1000;
// Фоновая проверка, пока есть связь: раз в 15 минут, при возврате в приложение
// и при появлении сети (не чаще раза в 2 минуты).
const AUTO_CHECK_INTERVAL_MS = 15 * 60 * 1000;
const AUTO_CHECK_MIN_GAP_MS = 2 * 60 * 1000;
// Перезагружаем, только когда человек минуту ничего не нажимал (или приложение
// свёрнуто) — не посреди ввода часов.
const IDLE_BEFORE_RELOAD_MS = 60 * 1000;

class VersionService {
    constructor() {
        this.currentVersion = null;
        this.checkInterval = null;
        this.onUpdateCallback = null;
    }

    // Initialize version checking (without auto-check)
    async init() {
        // Текущая — та, что запущена; запись в localStorage только для старых сборок без неё.
        this.currentVersion = RUNNING_VERSION || localStorage.getItem(CURRENT_VERSION_KEY);
        
        // Get initial version from server
        const serverVersion = await this.fetchServerVersion();
        
        if (serverVersion) {
            if (!this.currentVersion) {
                // First time - save version
                this.currentVersion = serverVersion;
                localStorage.setItem(CURRENT_VERSION_KEY, serverVersion);
            }
            // Don't auto-update on init, only on manual check or sync
        }

        // Don't start periodic checking by default
        // this.startVersionCheck();
    }

    // Manual check for updates
    // Вызывается после синка данных (кнопка Sync, вход) и кнопкой версии в шапке.
    // Сравниваем с ЗАПУЩЕННОЙ версией: раньше сравнивали с записью в localStorage,
    // которую ставили ещё до перезагрузки, — если после неё подгрузилась старая
    // сборка из кэша, приложение считало себя обновлённым и больше не пробовало.
    async checkForUpdate() {
        const serverVersion = await this.fetchServerVersion();
        const running = RUNNING_VERSION || this.currentVersion;

        if (serverVersion && serverVersion !== running) {
            if (this.recentlyTried(serverVersion)) {
                // Только что перезагружались ради этой версии, а запущена всё ещё
                // старая (кэш) — не перезагружаем снова, иначе синк после
                // обновления крутил бы перезагрузки по кругу.
                console.warn(`Update to ${serverVersion} was tried less than 10 min ago; still running ${running}`);
                return false;
            }
            console.log(`Version update detected: ${running} -> ${serverVersion}`);
            this.markTried(serverVersion);
            this.handleVersionUpdate(serverVersion);
            return true; // Update found
        }

        return false; // No update
    }

    recentlyTried(version) {
        try {
            const t = JSON.parse(localStorage.getItem(AUTO_UPDATE_KEY) || 'null');
            return !!t && t.version === version && Date.now() - t.at < AUTO_UPDATE_RETRY_MS;
        } catch {
            return false;
        }
    }

    markTried(version) {
        try { localStorage.setItem(AUTO_UPDATE_KEY, JSON.stringify({ version, at: Date.now() })); } catch { /* noop */ }
    }

    // Fetch version from server
    async fetchServerVersion() {
        try {
            // Add timestamp to prevent caching
            const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch version');
            }
            
            const data = await response.json();
            return data.version;
        } catch (error) {
            console.error('Error fetching version:', error);
            return null;
        }
    }

    // Start periodic version checking
    startVersionCheck() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
        }

        this.checkInterval = setInterval(() => { this.checkForUpdate(); }, VERSION_CHECK_INTERVAL);
    }

    // Handle version update
    handleVersionUpdate(newVersion) {
        // Update stored version
        localStorage.setItem(CURRENT_VERSION_KEY, newVersion);
        this.currentVersion = newVersion;

        // Mark that sync is needed after reload
        localStorage.setItem('sync_after_update', 'true');

        // Stop checking
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }

        // Call callback if registered
        if (this.onUpdateCallback) {
            this.onUpdateCallback(newVersion);
        } else {
            // Default behavior - show notification and reload
            this.showUpdateNotification(newVersion);
        }
    }

    // Show update notification
    showUpdateNotification(newVersion) {
        const message = `New version ${newVersion} is available. The application will reload in 5 seconds...`;
        
        // Show native notification if permission granted
        if (Notification.permission === 'granted') {
            new Notification('Application Update', {
                body: message,
                icon: '/favicon.ico'
            });
        }

        // Show in-app notification
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #4CAF50;
            color: white;
            padding: 16px 24px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 10000;
            font-family: Arial, sans-serif;
            font-size: 14px;
            max-width: 400px;
            animation: slideIn 0.3s ease-out;
        `;
        notification.innerHTML = `
            <div style="display: flex; align-items: center; gap: 12px;">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <div>
                    <div style="font-weight: bold; margin-bottom: 4px;">Update Available!</div>
                    <div>${message}</div>
                </div>
            </div>
        `;

        // Add animation
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideIn {
                from {
                    transform: translateX(400px);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
        `;
        document.head.appendChild(style);
        document.body.appendChild(notification);

        // Auto reload after 5 seconds
        setTimeout(() => {
            // Clear all caches and reload
            this.clearCachesAndReload();
        }, 5000);
    }

    // Clear caches and reload
    async clearCachesAndReload() {
        // Clear service worker caches
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
        }

        // Unregister service workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(
                registrations.map(registration => registration.unregister())
            );
        }

        // DON'T clear localStorage - keep all data!
        // Just ensure sync flag is set
        localStorage.setItem('sync_after_update', 'true');

        // Debug: log localStorage before reload
        console.log('LocalStorage before reload:', Object.keys(localStorage).length, 'items');
        console.log('LocalStorage contents:', {...localStorage});

        // Force reload from server - redirect to home page with timestamp
        // Timestamp ensures cache bypass and fresh load
        window.location.href = '/?t=' + Date.now();
    }

    // Register callback for version update
    onVersionUpdate(callback) {
        this.onUpdateCallback = callback;
    }

    // Get current version
    getCurrentVersion() {
        return this.currentVersion;
    }

    /**
     * Обновляться самому, пока есть связь, а не только по кнопке Sync: раньше
     * приложение, которое весь день не синкали, так и работало на старой версии
     * (10/2026, «не обновилась версия»).
     */
    startAutoUpdate() {
        if (typeof window === 'undefined' || this.autoTimer) return;
        this.lastActivity = Date.now();
        this.lastAutoCheck = 0;
        this.onActivity = () => { this.lastActivity = Date.now(); };
        ['pointerdown', 'keydown', 'touchstart'].forEach(ev => window.addEventListener(ev, this.onActivity, { passive: true, capture: true }));
        this.onWake = () => { if (document.visibilityState !== 'hidden') this.autoCheck(); };
        window.addEventListener('online', this.onWake);
        document.addEventListener('visibilitychange', this.onWake);
        this.autoTimer = setInterval(() => this.autoCheck(true), AUTO_CHECK_INTERVAL_MS);
    }

    async autoCheck(force = false) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        if (this.pendingVersion) { this.reloadWhenIdle(); return; }
        if (!force && Date.now() - (this.lastAutoCheck || 0) < AUTO_CHECK_MIN_GAP_MS) return;
        this.lastAutoCheck = Date.now();
        const serverVersion = await this.fetchServerVersion();
        const running = RUNNING_VERSION || this.currentVersion;
        if (!serverVersion || serverVersion === running || this.recentlyTried(serverVersion)) return;
        this.pendingVersion = serverVersion;
        this.reloadWhenIdle();
    }

    // Новая версия есть — ждём паузы во вводе (или свёрнутого приложения) и обновляемся.
    reloadWhenIdle() {
        clearTimeout(this.idleTimer);
        const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
        const idleFor = Date.now() - (this.lastActivity || 0);
        // Не перезагружаем посреди отправки часов: прерванная отправка оставляла
        // записи «в пути» (10/21).
        if (typeof window !== 'undefined' && window.__timesheetSyncBusy) {
            this.idleTimer = setTimeout(() => this.reloadWhenIdle(), 5000);
            return;
        }
        if (!hidden && idleFor < IDLE_BEFORE_RELOAD_MS) {
            this.idleTimer = setTimeout(() => this.reloadWhenIdle(), IDLE_BEFORE_RELOAD_MS - idleFor + 1000);
            return;
        }
        const version = this.pendingVersion;
        this.pendingVersion = null;
        if (!version || this.recentlyTried(version)) return;
        console.log(`Auto-update to ${version} (running ${RUNNING_VERSION || this.currentVersion})`);
        this.markTried(version);
        this.handleVersionUpdate(version);
    }

    // Stop version checking
    stop() {
        clearInterval(this.autoTimer);
        clearTimeout(this.idleTimer);
        this.autoTimer = null;
        if (this.onActivity) ['pointerdown', 'keydown', 'touchstart'].forEach(ev => window.removeEventListener(ev, this.onActivity, { capture: true }));
        if (this.onWake) {
            window.removeEventListener('online', this.onWake);
            document.removeEventListener('visibilitychange', this.onWake);
        }
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }
}

// Create singleton instance
const versionService = new VersionService();

export default versionService;